import React from "react";
import { Box } from "@mui/material";

const GRADIENT_ID = "mui-icon-gradient";

/**
 * Wraps any MUI icon with a linear gradient fill and optional gradient border container.
 *
 * Usage:
 *   <GradientIcon icon={EmojiEmotionsOutlinedIcon} size={40} />
 *   <GradientIcon icon={HubOutlinedIcon} size={40} bordered boxSize={80} borderRadius={24} borderWidth={2} />
 */
const GradientIcon = ({
  icon: IconComponent,
  size = 40,
  startColor = "#FF7FAC",
  endColor = "#5834BA",
  angle = "135",
  bordered = false,
  boxSize = 80,
  borderRadius = 24,
  borderWidth = 2,
  bgColor = "#FFFFFF",
  sx = {},
}) => {
  const rad = ((parseInt(angle) - 90) * Math.PI) / 180;
  const x2 = Math.round(((Math.cos(rad) + 1) / 2) * 100);
  const y2 = Math.round(((Math.sin(rad) + 1) / 2) * 100);
  const x1 = 100 - x2;
  const y1 = 100 - y2;

  const gradientId = `${GRADIENT_ID}-${startColor.replace(
    "#",
    ""
  )}-${endColor.replace("#", "")}`;
  const gradientCSS = `linear-gradient(${angle}deg, ${startColor}, ${endColor})`;

  const iconElement = (
    <Box sx={{ display: "inline-flex", position: "relative" }}>
      <svg width="0" height="0" style={{ position: "absolute" }}>
        <defs>
          <linearGradient
            id={gradientId}
            x1={`${x1}%`}
            y1={`${y1}%`}
            x2={`${x2}%`}
            y2={`${y2}%`}
          >
            <stop offset="0%" stopColor={startColor} />
            <stop offset="100%" stopColor={endColor} />
          </linearGradient>
        </defs>
      </svg>
      <IconComponent
        sx={{
          fontSize: size,
          fill: `url(#${gradientId})`,
        }}
      />
    </Box>
  );

  if (!bordered) {
    return <Box sx={sx}>{iconElement}</Box>;
  }

  // When bgColor is transparent, use outline approach instead of mask
  if (bgColor === "transparent") {
    return (
      <Box
        sx={{
          width: `${boxSize}px`,
          height: `${boxSize}px`,
          minWidth: `${boxSize}px`,
          borderRadius: `${borderRadius}px`,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: `${borderRadius}px`,
            padding: `${borderWidth}px`,
            background: gradientCSS,
            WebkitMask:
              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
          },
          ...sx,
        }}
      >
        {iconElement}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: `${boxSize}px`,
        height: `${boxSize}px`,
        minWidth: `${boxSize}px`,
        borderRadius: `${borderRadius}px`,
        padding: `${borderWidth}px`,
        background: gradientCSS,
        ...sx,
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: `${borderRadius - borderWidth}px`,
          backgroundColor: bgColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {iconElement}
      </Box>
    </Box>
  );
};

export default GradientIcon;
